<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { deriveConsolidatedRow, type IManagementArea } from '@/interfaces/entities/management'
import { useManagementStore } from '@/store/management'
import AreaAlarmDetail from './AreaAlarmDetail.vue'

const store = useManagementStore()

// "Linhas" = filhos da raiz única (Empresa → linhas) ou as próprias áreas-raiz.
const lines = computed<IManagementArea[]>(() => {
  if (store.roots.length === 1 && store.roots[0].children?.length) return store.roots[0].children
  return store.roots
})

const selectedAreaId = ref<number | null>(null)

const rows = computed(() =>
  lines.value.map((area) => {
    const indicators = store.areaIndicators(area.id)
    return {
      id: area.id,
      name: area.name,
      loading: !indicators && store.loadingIndicators,
      data: indicators ? deriveConsolidatedRow(indicators) : null,
    }
  }),
)

function selectArea(areaId: number) {
  selectedAreaId.value = selectedAreaId.value === areaId ? null : areaId
}

function formatDuration(minutes: number | null): string {
  if (minutes === null || minutes === undefined) return '—'
  if (minutes < 60) return `${Math.round(minutes)} min`
  const hours = Math.floor(minutes / 60)
  const mins = Math.round(minutes % 60)
  return mins ? `${hours}h ${mins}min` : `${hours}h`
}

function formatPercent(value: number | null): string {
  return value === null || value === undefined ? '—' : `${value.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`
}

function formatNumber(value: number | null): string {
  return value === null || value === undefined ? '—' : value.toLocaleString('pt-BR', { maximumFractionDigits: 0 })
}

function formatKwh(value: number | null): string {
  return value === null || value === undefined
    ? '—'
    : `${value.toLocaleString('pt-BR', { maximumFractionDigits: 0 })} kWh`
}

// Carrega os indicadores de cada linha conforme o filtro de período.
watch(
  () => [lines.value.map((l) => l.id).join(','), store.periodQuery] as const,
  () => lines.value.forEach((area) => store.fetchIndicators(area.id)),
  { immediate: true },
)

// Remove a seleção se a linha sumir da árvore.
watch(lines, (current) => {
  if (selectedAreaId.value && !current.some((l) => l.id === selectedAreaId.value)) selectedAreaId.value = null
})
</script>

<template>
  <div class="space-y-4">
    <div v-if="!lines.length" class="border border-stroke bg-slate-50 p-6 text-center text-sm text-slate-400">
      Nenhuma linha cadastrada. Crie áreas no módulo para visualizar os indicadores.
    </div>

    <div v-else class="overflow-x-auto border border-stroke bg-white">
      <table class="w-full text-left text-sm">
        <thead class="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th class="px-4 py-2.5">Linha</th>
            <th class="px-4 py-2.5 text-right">Utilização</th>
            <th class="px-4 py-2.5 text-right">Tempo total de parada</th>
            <th class="px-4 py-2.5 text-right">Qtd de paradas</th>
            <th class="px-4 py-2.5 text-right">Demais alarmes</th>
            <th class="px-4 py-2.5 text-right">Consumo</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in rows"
            :key="row.id"
            class="cursor-pointer border-t border-stroke transition hover:bg-primary/5"
            :class="selectedAreaId === row.id ? 'bg-primary/5' : ''"
            @click="selectArea(row.id)">
            <td class="px-4 py-2.5 font-medium text-primary underline-offset-2 hover:underline">{{ row.name }}</td>
            <template v-if="row.loading">
              <td colspan="5" class="px-4 py-2.5 text-center text-xs text-slate-400">calculando…</td>
            </template>
            <template v-else-if="row.data">
              <td class="px-4 py-2.5 text-right text-base font-bold text-slate-900">{{ formatPercent(row.data.utilizacao) }}</td>
              <td class="px-4 py-2.5 text-right font-semibold text-amber-700">{{ formatDuration(row.data.tempo_parada_min) }}</td>
              <td class="px-4 py-2.5 text-right text-slate-800">{{ formatNumber(row.data.paradas) }}</td>
              <td class="px-4 py-2.5 text-right text-slate-800">{{ formatNumber(row.data.demais_alarmes) }}</td>
              <td class="px-4 py-2.5 text-right font-semibold text-primary">{{ formatKwh(row.data.consumo_total) }}</td>
            </template>
            <template v-else>
              <td colspan="5" class="px-4 py-2.5 text-center text-xs text-slate-400">—</td>
            </template>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Detalhe da linha selecionada (paradas por classification type) -->
    <AreaAlarmDetail v-if="selectedAreaId" :area-id="selectedAreaId" />
  </div>
</template>
